import { BaseFeature } from '../../core/base-feature.js';

/**
 * UserPanelFeature - Frontend feature for controlling the desktop user panel
 *
 * Manages: panel visibility, assignment push/status, reward push/removal
 */
export class UserPanelFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        this.panelVisible = false;
        this.economyVisible = true;
        this.assignment = null;

        this.updateCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'user-panel',
            name: 'User Panel',
            version: 'v1.0',
            capabilities: [
                'panel_visibility',
                'push_assignment',
                'gem_rewards'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[UserPanel] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                return this._handleStateUpdate(payload);

            case 'assignment_update':
                return this._handleAssignmentUpdate(payload);

            // Response to requests
            case 'get_state_response':
            case 'set_panel_visible_response':
            case 'set_economy_visible_response':
            case 'push_assignment_response':
            case 'cancel_assignment_response':
            case 'approve_assignment_response':
                return this._resolveRequest(message);

            default:
                console.log('[UserPanel] Unknown command:', command);
        }
    }

    // ===== PUBLIC API =====

    /**
     * Send a command to the desktop and return a promise for the response
     */
    sendMessage(action, payload) {
        return new Promise((resolve, reject) => {
            const messageId = super.sendMessage(action, payload);

            this._pendingRequests.set(messageId, { resolve, reject });

            // Timeout after 8s
            setTimeout(() => {
                if (this._pendingRequests.has(messageId)) {
                    this._pendingRequests.delete(messageId);
                    resolve(null);
                }
            }, 8000);
        });
    }

    /**
     * Subscribe to state updates
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    // ===== INTERNAL =====

    _handleStateUpdate(data) {
        if (data.panelVisible !== undefined) this.panelVisible = data.panelVisible;
        if (data.economyVisible !== undefined) this.economyVisible = data.economyVisible;
        if (data.assignment !== undefined) this.assignment = data.assignment;

        this.updateCallbacks.forEach(cb => cb(data));
    }

    _handleAssignmentUpdate(data) {
        this.assignment = data.assignment ?? null;
        this.updateCallbacks.forEach(cb => cb({ assignment: this.assignment }));
    }

    _resolveRequest(message) {
        const { responseToId, payload } = message;
        const id = responseToId || payload?.originalMessageId || message.messageId;

        const pending = this._pendingRequests.get(id);
        if (pending) {
            this._pendingRequests.delete(id);
            pending.resolve(payload);
        }
    }

    cleanup() {
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}

export default UserPanelFeature;
